import type { ProposalOrder } from '@moment/horizon-data-access/proposal'
import type { Instrument, Lot } from '@/types/api/accounts'

import { OrderSide } from '@/types/enums/order'

// Returns the signed allocation quantity for the given account (and optional sleeve).
// - If sleeveId is provided, only allocations for that sleeve are considered.
// - Otherwise, all allocations for the account are summed.
// - The sign is determined by order.side (Sell => negative).
function getAllocationQuantityDelta(
  order: ProposalOrder,
  accountId: string,
  sleeveId?: string,
): number {
  const sign = order.side === OrderSide.Sell ? -1 : 1

  if (!order.allocations?.length) {
    return 0
  }

  const relevantAllocations = order.allocations.filter((allocation) => {
    if (allocation.moment_account_id !== accountId) return false
    if (sleeveId) {
      return allocation.sleeve_id === sleeveId
    }
    return true
  })

  const quantity = relevantAllocations.reduce((sum, a) => sum + a.quantity, 0)
  return sign * quantity
}

// Interface for proposal account holdings table data
// Only includes fields that are actually set and used by the columns
interface AccountPosition {
  // Required by ag-Grid for row identification
  id: string
  isin: string
  sleeve_ids: string[]
  par_value: number

  // Instrument fields displayed in columns
  description: string
  sp_rating: string
  moodys_rating: string | null
  current_price: number
  yield_to_worst: number
  sector: string
  cusip: string
  type: string
  maturity_date: string
  duration: number

  // Aggregated lot data
  open_quantity: number
  current_open_quantity: number

  // Proposal-specific fields
  proposed_action: string
  limit_price: number | null
  limit_ytw: number | null
  tax_effective_limit_ytw: number | null
  post_tax_limit_ytw: number | null
  eval_price: number
  eval_ytw: number
  corresponding_order: ProposalOrder | null
}

// Helper function to create base AccountPosition fields from instrument
function createAccountPosition(instrument: Instrument) {
  return {
    par_value: instrument.par_value,

    // Instrument fields
    description: instrument.description,
    sp_rating: instrument.sp_rating,
    moodys_rating: instrument.moodys_rating || null,
    current_price: instrument.price,
    yield_to_worst: instrument.yield_to_worst,
    sector: instrument.sector,
    cusip: instrument.cusip,
    type: instrument.type,
    maturity_date: instrument.maturity_date,
    duration: instrument.duration,

    // Default eval fields
    eval_price: instrument.price,
    eval_ytw: instrument.yield_to_worst,
  }
}

export function convertToPositions(
  lots: Lot[],
  previousLots: Lot[],
  proposalOrders: ProposalOrder[],
  accountId: string,
  sleeveId?: string,
): AccountPosition[] {
  const previousLotsMap: Record<string, number> = {}
  previousLots.forEach((lot) => {
    const isin = lot.instrument.isin

    if (!previousLotsMap[isin]) {
      previousLotsMap[isin] = lot.open_quantity
    } else {
      previousLotsMap[isin] = previousLotsMap[isin] + lot.open_quantity
    }
  })

  // Create current lots map for quantity lookup
  const currentLotsMap: Record<string, number> = {}
  const sleevesByIsin: Record<string, string[]> = {}
  lots.forEach((lot) => {
    const isin = lot.instrument.isin

    if (!currentLotsMap[isin]) {
      currentLotsMap[isin] = lot.open_quantity
    } else {
      currentLotsMap[isin] = currentLotsMap[isin] + lot.open_quantity
    }

    if (!sleevesByIsin[isin]) {
      sleevesByIsin[isin] = []
    }
    if (!sleevesByIsin[isin].includes(lot.sleeve_id)) {
      sleevesByIsin[isin].push(lot.sleeve_id)
    }
  })

  // Track which ISINs have orders
  const isinsWithOrders = new Set(
    proposalOrders.map((order) => order.instrument_id),
  )

  const positions: AccountPosition[] = []

  // Add ALL proposal orders (one per order)
  proposalOrders.forEach((order) => {
    const isin = order.instrument.isin
    const previousQuantity = previousLotsMap[isin] ?? 0
    const quantityDelta = getAllocationQuantityDelta(order, accountId, sleeveId)

    const position = {
      ...createAccountPosition(order.instrument as Instrument),
      id: order.moment_order_id,
      isin: isin,
      sleeve_ids: sleevesByIsin[isin] || [],
      open_quantity: previousQuantity + quantityDelta, // Use order quantity
      current_open_quantity: previousQuantity,
      proposed_action: order.side,
      limit_price: order.limit_price,
      limit_ytw: order.limit_ytw ?? null,
      tax_effective_limit_ytw: order.tax_effective_limit_ytw ?? null,
      post_tax_limit_ytw: order.post_tax_limit_ytw ?? null,
      corresponding_order: order,
    }

    positions.push(position)
  })

  // Add "hold" positions for ISINs where quantities are the same (no trading)
  Object.keys(currentLotsMap).forEach((isin) => {
    if (!isinsWithOrders.has(isin)) {
      const currentQuantity = currentLotsMap[isin]
      const previousQuantity = previousLotsMap[isin] ?? 0

      // Only add if it's a "hold" position (same quantities)
      if (currentQuantity === previousQuantity) {
        const lot = lots.find((l) => l.instrument.isin === isin)
        if (lot) {
          const position = {
            ...createAccountPosition(lot.instrument),
            id: isin,
            isin,
            sleeve_ids: sleevesByIsin[isin] || [],
            open_quantity: previousQuantity, // Not trading, so we use the previous quantity
            current_open_quantity: previousQuantity,
            proposed_action: 'hold',
            limit_price: null,
            limit_ytw: null,
            tax_effective_limit_ytw: null,
            post_tax_limit_ytw: null,
            corresponding_order: null,
          }

          positions.push(position)
        }
      }
    }
  })

  return positions
}
